/**
 * **옛 저장본의 컴포넌트 매개변수를 지금 꼴로** — 종류마다 한 번 적는다. 저장본을 불러오는 자리(`migrateProjectData` — 서비스의 불러오기·JSON
 * 들여오기)가 이 표 하나를 지난다. 진입 청크가 읽으므로 무거운 도메인(레지스트리·판독·방출)을 가져오지 않는다.
 *
 * 옛 꼴이 엔진에게 무슨 뜻이었는지 정확히 알 때만 적는다(옛 방출이 낸 덱과 같은 뜻으로 옮긴다 — 값을 짓지 않는다). 바뀐 것이 없으면 같은
 * 객체를 돌려준다. 다음 저장 때 새 꼴이 기록된다.
 */

import { withFlagDigit } from '@/domain/marsFlagWords';

/** MTPLJUN 세트의 유효 끝 정션 — W13 부재 = 자기 번호(그 행 하나). 덮음·정션 수·방출·옛 저장본 이행이 같은 정의를 쓴다(#384 R2) */
export const effectiveEndJunction = (j: { endJunction?: number | null; junctionNumber: number }): number =>
  j.endJunction ?? j.junctionNumber;

type Params = Record<string, unknown>;

/**
 * MTPLJUN — 옛 저장본은 정션 수 `njuns` 를 따로 두고 초기 유량을 세트마다 들었다(D-08·D-09 를 고치기 전). 옛 방출은 세트마다 CCC1NN1 을 그
 * 세트의 유효 끝 정션으로 냈다 — 세트마다 한 행인 초기 조건 표와 같은 뜻이다. 정션 수는 기하 세트가 덮는 수로 파생하므로 걷는다(옛 값이 그와
 * 달랐다면 옛 덱은 엔진이 거절했다). 이미 초기 조건 표가 있으면 그것을 지킨다.
 */
function upgradeMtpljun(params: Params): Params {
  const raw: unknown[] = Array.isArray(params.junctions) ? params.junctions : [];
  // 세트가 객체가 아닌 저장본(깨진 파일)은 꼴을 모른다 — 옮기지 않고 그대로 둔다(옛 꼴을 옮기는 자리라 깨진 꼴을 판정하지 않는다)
  if (raw.some((j) => j === null || typeof j !== 'object' || Array.isArray(j))) return params;
  const sets = raw as Array<Params & { junctionNumber: number; endJunction?: number }>;
  const legacyFlows = sets.some((j) => 'initialLiquidFlow' in j || 'initialVaporFlow' in j);
  if (!legacyFlows && !('njuns' in params)) return params;
  const { njuns: _njuns, ...rest } = params;
  void _njuns;
  const junctions = sets.map(({ initialLiquidFlow: _l, initialVaporFlow: _v, ...j }) => { void _l; void _v; return j; });
  const initialFlows = rest.initialFlows ?? (legacyFlows
    ? sets.map((j) => ({ liquidFlow: j.initialLiquidFlow, vaporFlow: j.initialVaporFlow, endJunction: effectiveEndJunction(j) }))
    : undefined);
  return { ...rest, junctions, ...(initialFlows !== undefined ? { initialFlows } : {}) };
}

/**
 * TMDPJUN — 옛 저장본은 수정 PV 항을 jefvcahs 와 따로 `useModifiedPvTerm` 으로 들었다(D-78 을 고치기 전). 옛 방출은 그것이 참이면 jefvcahs 를
 * `01000000`(e 자리만 1)으로 냈고, 거짓이면 jefvcahs 를 그대로 냈다 — 같은 덱이 되게 옮기고 따로 둔 칸은 걷는다.
 */
function upgradeTmdpjun(params: Params): Params {
  if (!('useModifiedPvTerm' in params)) return params;
  const { useModifiedPvTerm, ...rest } = params;
  return useModifiedPvTerm === true ? { ...rest, jefvcahs: withFlagDigit('jefvcahs', undefined, 'e', 1) } : rest;
}

export const SAVED_PARAMETER_UPGRADES: Readonly<Record<string, (params: Params) => Params>> = {
  mtpljun: upgradeMtpljun,
  tmdpjun: upgradeTmdpjun,
};

type Appearance = Record<string, unknown>;

/**
 * 터빈 — 옛 저장본은 «Reverse»(흐름 방향 HP↔LP 뒤집기)를 `flipY` 로 들었다(D-185 를 고치기 전 — 폼이 그 칸에 썼다). 뒤집기의 축은 저장값이
 * 정하므로(`flipX` 좌우·`flipY` 상하) 그 뜻은 `flipX` 다. 터빈의 상하 뒤집기는 화면에 낸 적이 없어 옛 `flipY` 는 늘 «Reverse» 였다.
 */
function upgradeTurbineAppearance(appearance: Appearance): Appearance {
  if (appearance.flipY !== true) return appearance;
  const { flipY: _flipY, ...rest } = appearance;
  void _flipY;
  return { ...rest, flipX: true };
}

/** **옛 저장본의 노드 외형을 지금 꼴로** — 종류마다 한 번 적는다(`SAVED_PARAMETER_UPGRADES` 와 같은 규율, 같은 불러오기 자리) */
export const SAVED_APPEARANCE_UPGRADES: Readonly<Record<string, (appearance: Appearance) => Appearance>> = {
  turbine: upgradeTurbineAppearance,
};

/**
 * **옛 저장본의 모델 설정에서 걷는 키** — 지금 아무도 읽지 않는데, 남겨 두면 저장이 불러온 설정을 이어 받아 사본을 계속 싣는 키다. 키마다 까닭을
 * 적는다(`SAVED_PARAMETER_UPGRADES` 와 같은 규율, 같은 불러오기 자리 — `projectMigration.migrateProjectData`).
 */
export const RETIRED_MODEL_SETTINGS_KEYS: Readonly<Record<string, string>> = {
  workingFluid: '첫 계통 카드 유체의 사본이었다 — 정본은 settings.systems[].fluid, 없으면 h2o 를 지었다(D-203)',
  simulationType: 'card100 계산 종류의 사본이었다 — 정본은 settings.card100.calculationType, 없으면 transnt 를 지었다(D-162)',
  unitSystem: 'card102 입력 단위의 사본이었다 — 정본은 settings.card102.inputUnits, 없으면 si 를 지었다(D-162)',
  minDt: '옛 «프로젝트 설정» 의 최소 시간 간격 — 덱·화면 어디서도 읽지 않았고 없으면 1e-6 을 지었다. 시간 간격의 정본은 시간 단계(settings.timePhases[])다(D-162)',
  maxDt: '옛 «프로젝트 설정» 의 최대 시간 간격 — 덱·화면 어디서도 읽지 않았고 없으면 0.1 을 지었다. 시간 간격의 정본은 시간 단계(settings.timePhases[])다(D-162)',
};
